/**
 * ContextGuard
 *
 * Protects routes based on the multi-context model.
 * Checks if the user has the required context and (optionally) the required
 * role within that context.
 *
 * Must be used inside AuthGuard (assumes user is already authenticated).
 *
 * @example
 * // School routes — user must have school context with admin or teacher role
 * <ContextGuard context="school" roles={['school_admin', 'head_school_admin', 'teacher']}>
 *   <SchoolDashboard />
 * </ContextGuard>
 *
 * @example
 * // Platform admin routes — user must have platform context with admin role
 * <ContextGuard context="platform" roles={['admin']}>
 *   <UserManagement />
 * </ContextGuard>
 *
 * @example
 * // Vendor routes — user just needs business context (any role)
 * <ContextGuard context="business">
 *   <VendorDashboard />
 * </ContextGuard>
 */

import { Navigate, Link as RouterLink } from 'react-router-dom'
import { FiLock } from 'react-icons/fi'
import type { ContextType } from '../../types/context.types'
import { useAuthStore } from '../../store'
import { hasContext, hasRoleInContext } from '../../lib/contextHelpers'

interface ContextGuardProps {
  children: React.ReactNode
  /** Required context type */
  context: ContextType
  /** Optional: required roles within that context */
  roles?: string[]
  /** Where to redirect if access is denied */
  redirectTo?: string
  /** Optional fallback component instead of redirect */
  fallback?: React.ReactNode
}

function AccessDeniedFallback() {
  return (
    <div className="p-8 max-w-md mx-auto mt-20 text-center bg-background rounded-lg border border-border">
      <div className="flex flex-col items-center gap-4">
        <FiLock className="h-12 w-12 text-muted-foreground" />
        <p className="text-xl font-semibold text-foreground">
          Access Denied
        </p>
        <p className="text-muted-foreground">
          You don't have the required permissions to access this page.
        </p>
        <RouterLink to="/">
          <button className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md font-medium">
            Go Back Home
          </button>
        </RouterLink>
      </div>
    </div>
  )
}

export function ContextGuard({
  children,
  context,
  roles,
  redirectTo,
  fallback,
}: ContextGuardProps) {
  const user = useAuthStore((s) => s.user)

  if (!user) {
    if (redirectTo) return <Navigate to={redirectTo} replace />
    return <>{fallback ?? <AccessDeniedFallback />}</>
  }

  // Check if user has the required context
  if (!hasContext(user, context)) {
    if (redirectTo) return <Navigate to={redirectTo} replace />
    return <>{fallback ?? <AccessDeniedFallback />}</>
  }

  // If specific roles are required, check them
  if (roles && roles.length > 0) {
    if (!hasRoleInContext(user, context, roles)) {
      if (redirectTo) return <Navigate to={redirectTo} replace />
      return <>{fallback ?? <AccessDeniedFallback />}</>
    }
  }

  return <>{children}</>
}
